import { callOAuthService } from "./componentApi";
import { uuidv4 } from "./util";

export type Base64String = string;
export type FileID = string;
export type FileContent = File | Base64String | Blob | ArrayBuffer;

const CHUNK_SIZE = 40 * 1024 * 1024; // 40MB

export async function uploadFile(file: FileContent, callback: (id: FileID) => Promise<void>, progress?: (p: number) => void): Promise<void> {
    const fileForUpload = prepareForUpload(file);
    const chunkCount = Math.ceil(fileForUpload.size / CHUNK_SIZE);

    const fileId = fileForUpload.name;

    progress?.(0);
    for (let i = 0; i < chunkCount; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileForUpload.size);
        const chunk = fileForUpload.slice(start, end);

        await uploadChunk(chunk, start, end, fileForUpload);

        progress?.(100 * i / chunkCount);
    }
    progress?.(100);

    await callback(fileId);
}

async function uploadChunk(chunk: Blob, start: number, end: number, file: File) {
    return new Promise<void>((resolve, reject) => {
        const form = new FormData();
        form.append('file', chunk, file.name);
    
        const request = new XMLHttpRequest();
        request.open('POST', 'ST_UploadFile.aspx', true);
        request.setRequestHeader('Content-Range', `bytes ${start}-${end}/${file.size}`);
        request.onload = function() {
            resolve();
        }
        request.send(form);
    })
}

export async function attachFile(srcName: string, identity: string, file: File | Base64String | Blob | ArrayBuffer) {
    const fileForUpload = prepareForUpload(file);

    await uploadFile(fileForUpload, async (fileId) => {
        await callOAuthService('ST.Synergy', 'AttachFile', {
            srcName,
            identity,
            fileId,
        })
    })
}

function prepareForUpload(content: FileContent): File {
    // Make sure to set a file name if the content is not a file.
    if (content instanceof File) {
        return content;
    }

    if (typeof content === 'string') {
        return fileFromBase64(content);
    }

    if (content instanceof Blob) {
        return new File([content], 'file');
    }

    if (content instanceof ArrayBuffer) {
        return new File([content], 'file');
    }

    throw new Error('Unsupported file content');
}

export function fileFromBase64(base64String: Base64String) {
    // Split the base64 string if it includes metadata like "data:image/png;base64,"
    const [meta, base64Data] = base64String.split(',');

    // Decode the base64 string into binary data
    const byteCharacters = atob(base64Data || base64String);

    // Convert binary string into an array of bytes
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    // Create a Uint8Array from the byte numbers array
    const byteArray = new Uint8Array(byteNumbers);

    const mime = meta ? meta.split(';')[0].split(':')[1] : 'application/octet-stream';

    const extension = mimeToExtension(mime);
    const fileName = extension ? `${uuidv4()}.${extension}` : uuidv4();
    // Create a new file using the byteArray, file name, and mime type
    return new File([byteArray], fileName, { type: mime });
}

export function mimeToExtension(mime: string): string {
    switch (mime) {
        case 'image/png':
            return 'png';
        case 'image/jpeg':
            return 'jpeg';
        case 'image/gif':
            return 'gif';
        case 'image/bmp':
            return 'bmp';
        case 'image/webp':
            return 'webp';
        case 'text/markdown':
            return 'md';
        default:
            return '';
    }
}




